import styled from '@emotion/styled'
import { Helper } from '@utils/index'
import React, { useEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown } from 'lucide-react'
import {
  Dropdown,
  DropdownContainer,
  DropdownItem,
  StatusTag,
  StatusTagWrapper,
} from './style'

interface StatusProps {
  item: { status?: string }
  statuses?: string[]
  onStatusChange?: (val: string) => void
}

const Status = ({ item, statuses = [], onStatusChange }: StatusProps) => {
  const initialStatus = item?.status ?? 'Pending'

  const [selectedStatus, setSelectedStatus] = useState(initialStatus)
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 })
  const tagRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLUListElement>(null)

  if (!initialStatus) return <span style={{ color: '#999' }}>-</span>

  const isStaticStatus =
    selectedStatus === 'Completed' ||
    selectedStatus === 'Cancelled' ||
    selectedStatus === 'Solved' ||
    selectedStatus === 'Delivered' ||
    selectedStatus === 'Refund'||
    selectedStatus === 'Refunded'

  const { text, bg } = Helper.getStatusColor(selectedStatus || initialStatus)

  useEffect(() => {
    setSelectedStatus(item?.status ?? '')
  }, [item?.status])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        tagRef.current &&
        !tagRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }

    const handleScroll = (e: Event) => {
      if (open) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      window.addEventListener('scroll', handleScroll, true) // capture phase for any scrolling element
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', handleScroll, true)
    }
  }, [open])

  const handleStatusChange = (status: string) => {
    if (status === selectedStatus) {
      setOpen(false)
      return
    }

    if (onStatusChange && !isStaticStatus) {
      onStatusChange(status)
    }

    setSelectedStatus(status)
    setOpen(false)
  }

  const isDropdownEnabled = statuses.length > 0 && !isStaticStatus

  const toggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!isDropdownEnabled) return

    if (!open && tagRef.current) {
      const rect = tagRef.current.getBoundingClientRect()

      // Calculate position relative to viewport
      let dropdownTop = rect.bottom + 6
      const dropdownHeightGuesses = statuses.length * 35 + 20 // approx height

      // If there's not enough room at the bottom, display it above the tag
      if (
        typeof window !== 'undefined' &&
        dropdownTop + dropdownHeightGuesses > window.innerHeight
      ) {
        dropdownTop = rect.top - dropdownHeightGuesses
      }

      setCoords({
        top: Math.max(0, dropdownTop),
        left: rect.left,
        width: rect.width,
      })
    }
    setOpen(!open)
  }

  return (
    <StatusTagWrapper>
      <DropdownContainer>
        <StatusTag
          ref={tagRef}
          textColor={text}
          bgColor={bg}
          onClick={toggleOpen}
          style={{
            cursor: isDropdownEnabled ? 'pointer' : 'default',
            opacity: isStaticStatus ? 0.7 : 1,
            userSelect: 'none',
          }}
        >
          {selectedStatus}
          {isDropdownEnabled && (
            <ChevronDown size={16} style={{ marginLeft: 6 }} />
          )}
        </StatusTag>

        {open &&
          isDropdownEnabled &&
          typeof document !== 'undefined' &&
          createPortal(
            <Dropdown
              ref={dropdownRef}
              style={{
                position: 'fixed',
                top: coords.top,
                left: coords.left,
                minWidth: Math.max(120, coords.width),
                margin: 0,
                zIndex: 99999,
              }}
            >
              {statuses
                .filter((s) => s !== selectedStatus)
                .map((status) => {
                  const { text, bg } = Helper.getStatusColor(status)
                  return (
                    <DropdownItem
                      key={status}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleStatusChange(status)
                      }}
                      textColor={text}
                      bgColor={bg}
                    >
                      {status}
                    </DropdownItem>
                  )
                })}
            </Dropdown>,
            document.body,
          )}
      </DropdownContainer>
    </StatusTagWrapper>
  )
}

export default Status
